import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import PadStatus from './PadStatus';
import PadFooter from './PadFooter';
import { LoadingOrderOpsStatus } from 'constants/orderConstants';
import { AircraftStatusPill } from '../../../components/AircraftStatusPill';
import { STORAGE_LOCATIONS } from '../models/OrderStorageLocations';

// Simplified CountdownTimer using order.takeoffTimeMs
const CountdownTimer = ({ order }) => {
  const [seconds, setSeconds] = useState(0);
  const [timeText, setTimeText] = useState("");

  // Define updateTimer with useCallback to avoid recreating it on every render
  const updateTimer = React.useCallback(() => {
    if (!order?.takeOffTimeMs) {
      setTimeText("");
      return;
    }

    const now = Date.now();
    const diffInSeconds = Math.floor((order.takeOffTimeMs - now) / 1000);
    
    setSeconds(diffInSeconds);
    
    if (diffInSeconds <= 0) {
      setTimeText("");
    } else if (diffInSeconds < 60) {
      setTimeText(`${diffInSeconds}s`);
    } else {
      const minutes = Math.floor(diffInSeconds / 60);
      const remainingSeconds = diffInSeconds % 60;
      setTimeText(`${minutes}m ${remainingSeconds}s`);
    }
  }, [order?.takeOffTimeMs]);

  useEffect(() => {
    // Only start countdown if we have a takeoff time
    if (!order?.takeOffTimeMs) {
      setTimeText("");
      return;
    }

    // Update immediately
    updateTimer();
    
    // Then update every second
    const intervalId = setInterval(updateTimer, 1000);
    
    return () => clearInterval(intervalId);
  }, [order?.takeOffTimeMs, updateTimer]);

  return timeText ? (
    <Typography className="text-xl font-medium mt-1">
      {`Takeoff in ${timeText}`}
    </Typography>
  ) : null;
};

interface PadColumnProps {
  padName: string;
  padId: number;
  order1ShortId?: string;
  order1Status?: LoadingOrderOpsStatus;
  order1TimeSinceOrderModified?: string; //Timestamp - last time the order was modifed - if we are in received we can use this info to see how long we are in received 
  order2ShortId?: string;
  switchedToPadName?: string;
  aircraftStatus?: string;
  order1?: any;
  pad: any;
  order2?: any;
  alerts?: {[key: string]: string};
  padChangedNotifications: Record<string, { orderId: string; fromPadId: number; fromPadName: string; toPadId: number; toPadName: string }>;
  isOrder1Resending?: boolean;
}

const PadColumn = ({
  padName,
  padId,
  order1ShortId,
  order1Status,
  order1TimeSinceOrderModified,
  order2ShortId,
  switchedToPadName,
  aircraftStatus,
  order1,
  pad,
  order2,
  alerts,
  padChangedNotifications,
  isOrder1Resending,
}: PadColumnProps) => {

  // Create store object from order's SSE data
  const store = order1 ? {
    logo: (order1 as any).merchantImageUrl,
    name: order1.outletName
  } : null;

  // Create store object for next order from SSE data
  const nextOrderStore = order2 ? {
    logo: (order2 as any).merchantImageUrl,
    name: order2.outletName
  } : null;

  // Storage location display component
  const StorageLocationEmojis = () => {
    if (!order1 || !order1.bagLocations || order1.bagLocations.length === 0) return null;
    
    // Create an array to hold all location icons, including duplicates
    const locationIconsToRender = [];

    // Parse all locations from the bagLocations array
    const allLocations = order1.bagLocations
                          .flatMap(locString => locString.split(','))
                          .map(loc => loc.trim());
    
    // For each location in STORAGE_LOCATIONS
    STORAGE_LOCATIONS.forEach(location => {
      // Count how many times this location appears in the parsed locations
      const locationCount = allLocations.filter(
        loc => loc.toLowerCase() === location.id.toLowerCase()
      ).length;
    
      // Add this location to our array the appropriate number of times
      for (let i = 0; i < locationCount; i++) {
        locationIconsToRender.push(
          <div 
            key={`${location.id}-${i}`}
            className="rounded-full bg-white w-12 h-12 flex items-center justify-center mr-2 border border-gray-200"
          >
            <span className="text-2xl">{location.emoji}</span>
          </div>
        );
      }
    });
    
    return (
      <div className="flex items-center">
        {locationIconsToRender}
      </div>
    );
  };

  return (
    <div className={`flex flex-col border-r h-full border-mannamono-line justify-between p-6 ${
      order1Status === LoadingOrderOpsStatus.DISPATCHED ||
      order1Status === LoadingOrderOpsStatus.DELIVERED
        ? 'bg-mannamono-bg'
        : 'bg-white'
    }`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-medium">Pad {padName}</h1>
          {aircraftStatus && <AircraftStatusPill status={aircraftStatus} size="large" />}
          {order1 && <CountdownTimer order={order1} />}
        </div>
        {/* Storage location indicators */}
        <StorageLocationEmojis />
      </div>
      {/* Pad change notifications - show orders that left this pad */}
      {Object.entries(padChangedNotifications)
        .filter(([_, notification]) => notification.fromPadId === padId)
        .map(([orderId, notification]) => (
          <div key={orderId} className="mt-1 p-1 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded text-xl">
            ℹ️ Order {notification.orderId} moved from {notification.fromPadName} to {notification.toPadName}
          </div>
        ))
      }
      {order1ShortId && alerts && Object.entries(alerts).map(([id, message]) => {
        const lines = message.split('\n');
        return (
          <div key={id} className="mt-2 p-2 bg-pink-100 border border-pink-400 text-pink-700 rounded text-3xl" style={{ animation: 'flash 1s infinite' }}>
            <div>⚠️ {lines[0]}</div>
            {lines.slice(1).map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      })}
      <div className="relative">
        <PadStatus
          orderNumber={order1ShortId}
          loadingStatus={order1Status}
          switchingPadName={switchedToPadName}
          store={store}
        />
      </div>
      {/* Resending order badge */}
      {isOrder1Resending && (
        <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-xl font-semibold flex items-center">
          <span className="mr-2">🔄</span>
          <span>Resending Order</span>
        </div>
      )}
      <PadFooter
        nextOrder={order2ShortId}
        timeSinceOrderModified={order1TimeSinceOrderModified}
        loadingStatus={order1Status}
        store={nextOrderStore}
        order1ShortId={order1ShortId}
      />
      <style>
        {`
          @keyframes flash {
            0%, 100% { display: block; }
            50% { display: none; }
          }
        `}
      </style>
    </div>
  );
};

export default PadColumn;